import { useState } from 'react';
import { type SelectSingleEventHandler } from 'react-day-picker';
import styled from 'styled-components';
import { DateCalendar } from '@m/alchemy-ui/DateCalendar';
import { Popper } from '@m/alchemy-ui/Popper';
import { useIntl } from 'react-intl';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { Button } from '@m/alchemy-ui/Button';
import { type AnnouncementCalendarFormProps } from '../../features';

const StyledAnnouncementButton = styled(Button)`
  && {
    font-weight: normal;
    text-decoration: none;
    background-color: transparent;
    border-color: transparent;
    padding: 6px 0px;
    white-space: pre-wrap;

    &:hover,
    &:focus,
    &:active {
      background-color: transparent !important;
      border-color: transparent !important;
      box-shadow: none !important;
    }
  }
`;

function formatDate(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(dateStr?: string): Date | null {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function formatDisplayDate(date: Date | null, locale: string): string {
  if (!date) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

export const AnnouncementCalendarForm = ({
  announcementStart,
  announcementExpire,
  onChange,
  errors
}: AnnouncementCalendarFormProps) => {
  const intl = useIntl();
  const [start, setStart] = useState<Date | null>(parseDate(announcementStart));
  const [end, setEnd] = useState<Date | null>(parseDate(announcementExpire));

  const [showStartPicker, setShowStartPicker] = useState(false);
  const [showEndPicker, setShowEndPicker] = useState(false);

  const onDateChange = (start: Date | null, end: Date | null) => {
    onChange?.({
      start: formatDate(start) || null,
      end: formatDate(end) || null
    });
  };

  const handleStartChange: SelectSingleEventHandler = (date) => {
    setShowStartPicker(false);
    if (!date) return;
    setStart(date);
    onDateChange(date, end);
  };

  const handleEndChange: SelectSingleEventHandler = (date) => {
    setShowEndPicker(false);
    if (!date) return;
    setEnd(date);
    onDateChange(start, date);
  };

  return (
    <>
      <div className="form_box">
        <h3>{intl.formatMessage({ id: 'start', defaultMessage: 'Start' })}</h3>
        <Popper
          placement="right-start"
          isOpen={showStartPicker}
          onClose={() => setShowStartPicker(false)}
          trigger={
            <StyledAnnouncementButton
              priority="tertiary"
              href="#"
              id="announcement_start_button"
              onClick={(e) => {
                e.preventDefault();
                setShowStartPicker((v) => !v);
                setShowEndPicker(false);
              }}
            >
              {intl.formatMessage({ id: 'announcementStarts', defaultMessage: 'Announcement starts' })}{' '}
              <span id="announcement_start_text">{formatDisplayDate(start, intl.locale)}</span>
            </StyledAnnouncementButton>
          }
          overlay={
            <PopperBox maxWidth="auto" style={{ textAlign: 'left' }}>
              <DateCalendar
                mode="single"
                defaultMonth={start ?? undefined}
                selected={start ?? undefined}
                onSelect={handleStartChange}
              />
            </PopperBox>
          }
        />
        {errors?.announcement_start && <span className="error">{errors.announcement_start}</span>}
      </div>

      <div className="form_box">
        <h3>{intl.formatMessage({ id: 'expire', defaultMessage: 'Expire' })}</h3>
        <Popper
          placement="right-start"
          isOpen={showEndPicker}
          onClose={() => setShowEndPicker(false)}
          trigger={
            <StyledAnnouncementButton
              priority="tertiary"
              href="#"
              id="announcement_expire_button"
              onClick={(e) => {
                e.preventDefault();
                setShowEndPicker((v) => !v);
                setShowStartPicker(false);
              }}
            >
              {intl.formatMessage({ id: 'announcementExpires', defaultMessage: 'Announcement expires' })}{' '}
              <span id="announcement_expire_text">{formatDisplayDate(end, intl.locale)}</span>
            </StyledAnnouncementButton>
          }
          overlay={
            <PopperBox maxWidth="auto" style={{ textAlign: 'left' }}>
              <DateCalendar
                mode="single"
                defaultMonth={end ?? undefined}
                selected={end ?? undefined}
                onSelect={handleEndChange}
              />
            </PopperBox>
          }
        />
        {intl.formatMessage({ id: 'atTheEndOfThatDay', defaultMessage: 'at the end of that day' })}
      </div>
    </>
  );
};
